import { useCallback, useEffect, useRef, useState } from "react";

import { getContactsAPI, getContactAPI } from "services/api/contacts/api";
import { Contact, ContactRow } from "services/api/contacts/types";
import { initialPagination } from "ui/components/Table/consts";

const PAGE_SIZE = 20;

// Svaki ContactDropdown drži vlastiti dohvat, pa kreiranje kontakta u jednom dropdownu
// mora invalidirati liste u svim ostalima (npr. kontakt osiguranja i kontakt kaska na istom koraku).
let contactsVersion = 0;
const versionListeners = new Set<(version: number) => void>();

/** Označi listu kontakata zastarjelom — svi montirani ContactDropdown-ovi ponovno dohvaćaju prvu stranicu. */
export const invalidateContacts = () => {
  contactsVersion += 1;
  versionListeners.forEach((listener) => listener(contactsVersion));
};

/**
 * Paginirani dohvat kontakata s infinite-scrollom.
 * Prva stranica se dohvaća (ponovno) kad se promijeni pretraga / tip / grupe ili kad netko
 * pozove `invalidateContacts`; `loadMore` dodaje iduće stranice.
 */
export const usePaginatedContacts = (
  search: string,
  contactTypeId: string | null,
  contactGroupIds?: string[]
) => {
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [version, setVersion] = useState(contactsVersion);

  useEffect(() => {
    versionListeners.add(setVersion);
    // Lista je možda invalidirana dok ovaj dropdown nije bio montiran.
    setVersion(contactsVersion);

    return () => {
      versionListeners.delete(setVersion);
    };
  }, []);

  // Sprječava out-of-order rezultate kad se filteri promijene usred dohvata.
  const requestIdRef = useRef(0);
  const groupsKey = contactGroupIds?.join(",") ?? "";

  const fetchPage = useCallback(
    async (pageNumber: number) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const res = await getContactsAPI(
          { ...initialPagination, pageNumber, pageSize: PAGE_SIZE },
          search,
          contactTypeId,
          contactGroupIds
        );

        if (requestId !== requestIdRef.current) return;

        setContacts((prev) => (pageNumber === 1 ? res.data : [...prev, ...res.data]));
        setTotalPages(
          res.pagination.totalPages ??
            Math.max(1, Math.ceil((res.pagination.totalCount ?? res.data.length) / PAGE_SIZE))
        );
        setPage(pageNumber);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    // groupsKey pokriva contactGroupIds (stabilan primitiv umjesto reference);
    // version prisiljava ponovni dohvat nakon kreiranja kontakta u drugom dropdownu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, contactTypeId, groupsKey, version]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isLoading || page >= totalPages) return;
    fetchPage(page + 1);
  }, [isLoading, page, totalPages, fetchPage]);

  const refresh = useCallback(() => fetchPage(1), [fetchPage]);

  return { contacts, isLoading, hasMore: page < totalPages, loadMore, refresh };
};

/**
 * Dohvaća kontakt po id-u kad nije među već učitanim kontaktima — da se preselektirana vrijednost
 * (npr. kontakt s neke kasnije stranice) prikaže u inputu.
 */
export const usePreselectedContact = (
  value: string | null | undefined,
  loadedContacts: ContactRow[]
): Contact | null => {
  const [contact, setContact] = useState<Contact | null>(null);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!value) {
      setContact(null);
      fetchedIdRef.current = null;
      return;
    }
    if (fetchedIdRef.current === value) return;
    if (loadedContacts.some((c) => c.id === value)) return;

    fetchedIdRef.current = value;
    let active = true;
    getContactAPI(value)
      .then((c) => active && setContact(c))
      .catch(() => active && setContact(null));

    return () => {
      active = false;
    };
    // loadedContacts namjerno NIJE u deps — inače promjena stranice (novi dohvat) prekine ovaj dohvat prije nego stigne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return value ? contact : null;
};
